import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload, Sparkles, CheckCircle2, AlertTriangle, FileText, MapPin, Calendar, Save, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal IA para subir factura del externo y cazar contra trabajos.
 *
 * Flujo:
 * 1) Subes el PDF → Gemini Vision extrae líneas y propone matches con discrepancias de precio.
 * 2) Tú revisas (puedes desmarcar líneas dudosas) → ves si te cuadra el total.
 * 3) Pulsas "Aprobar y guardar" → se persiste y las paradas quedan marcadas como facturadas por externo.
 *
 * Props:
 *  - colaborador: { id, nombre, autonomo } o null
 *  - onClose: () => void
 *  - onAprobada?: () => void   // callback opcional al aprobar
 */
export default function CazarFacturaDialog({ colaborador, onClose, onAprobada }) {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [excluidos, setExcluidos] = useState({}); // {idx: true} → líneas que el usuario quiere NO aprobar
  const [notas, setNotas] = useState("");
  const [guardando, setGuardando] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (máx 20MB)");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const submit = async () => {
    if (!file) { toast.error("Selecciona primero un archivo"); return; }
    setProcessing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/colaboradores/${colaborador.id}/cazar-factura`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(data);
      toast.success(`Factura analizada · ${data.resumen.lineas_matcheadas}/${data.resumen.lineas_factura} líneas cuadradas`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); setExcluidos({}); setNotas(""); };

  const toggleExcluir = (idx) => {
    setExcluidos((p) => ({ ...p, [idx]: !p[idx] }));
  };

  const aprobar = async () => {
    if (!result) return;
    setGuardando(true);
    try {
      const matches_aprobados = result.matches
        .map((m, idx) => ({ m, idx }))
        .filter(({ idx }) => !excluidos[idx])
        .map(({ m }) => {
          const t = m.match?.trabajo;
          const d = m.discrepancia;
          return {
            linea_factura: m.linea_factura,
            ruta_id: t?.ruta_id || null,
            parada_idx: t?.parada_idx ?? null,
            linea_id: t?.linea_id || null,
            peticion_id: t?.peticion_id || null,
            peticion_codigo: t?.peticion_codigo || null,
            score: m.match?.score ?? null,
            precio_acordado: t?.precio_acordado ?? null,
            diferencia: d?.diferencia ?? null,
            porcentaje: d?.porcentaje ?? null,
            nivel: m.match ? (d?.nivel || "ok") : "sin_match",
          };
        });
      const { data } = await api.post(`/colaboradores/${colaborador.id}/facturas-cazadas`, {
        factura: result.factura,
        matches_aprobados,
        notas: notas || null,
        resumen: result.resumen,
      });
      toast.success(`Factura guardada · ${data.totales.lineas_matcheadas} paradas marcadas como facturadas`);
      onAprobada?.(data);
      onClose?.();
    } catch (e) {
      toast.error("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGuardando(false);
    }
  };

  // Helpers de discrepancia
  const renderDiscrepancia = (d) => {
    if (!d) return <span className="text-[10px] text-zinc-500">—</span>;
    if (d.nivel === "sin_precio") {
      return (
        <div className="text-[10px] text-zinc-400" title="No hay precio acordado en la línea ni tarifa del colaborador">
          <span className="bg-zinc-700/40 text-zinc-300 px-1 py-0.5 rounded">sin precio</span>
        </div>
      );
    }
    const color =
      d.nivel === "ok" ? "text-emerald-300 bg-emerald-500/10" :
      d.nivel === "warning" ? "text-amber-300 bg-amber-500/10" :
      "text-red-300 bg-red-500/15";
    const Icon = (d.diferencia || 0) > 0 ? TrendingUp : (d.diferencia || 0) < 0 ? TrendingDown : CheckCircle2;
    const signo = (d.porcentaje || 0) > 0 ? "+" : "";
    const origenTxt = d.precio_acordado_origen === "tarifa_dia_colaborador"
      ? "(tarifa día)"
      : d.precio_acordado_origen === "coste_montaje_linea" ? "(coste línea)" : "";
    return (
      <div className={`inline-flex flex-col items-end px-1.5 py-0.5 rounded ${color}`}>
        <div className="text-[11px] font-mono font-semibold flex items-center gap-1">
          <Icon className="w-3 h-3" /> {signo}{(d.diferencia ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}€
        </div>
        <div className="text-[9px] opacity-80">
          {signo}{(d.porcentaje ?? 0).toFixed(1)}% · {(d.precio_acordado ?? 0).toLocaleString("es-ES")}€ {origenTxt}
        </div>
      </div>
    );
  };

  // Totales para el footer
  const totalesAprobar = React.useMemo(() => {
    if (!result) return null;
    let nLin = 0, totFact = 0, totAcord = 0, sinPrecio = 0;
    result.matches.forEach((m, idx) => {
      if (excluidos[idx]) return;
      nLin++;
      totFact += Number(m.linea_factura?.importe || 0);
      if (m.discrepancia?.precio_acordado) totAcord += Number(m.discrepancia.precio_acordado);
      if (m.discrepancia?.nivel === "sin_precio") sinPrecio++;
    });
    return { nLin, totFact, totAcord, diff: totFact - totAcord, sinPrecio };
  }, [result, excluidos]);

  if (!colaborador) return null;

  return (
    <Dialog open={!!colaborador} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            Cazar factura de {colaborador.nombre}
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 text-xs text-violet-200/90 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
              <div>
                Sube la factura (PDF o foto) que te ha emitido este colaborador. La IA extraerá automáticamente
                las líneas con fecha + PDV + importe y las cruzará con las paradas que ha hecho en tus rutas.
              </div>
            </div>

            <label htmlFor="cf-file" className="block cursor-pointer">
              <div className={`bg-zinc-900/60 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                file ? "border-emerald-500/50" : "border-zinc-700 hover:border-amber-500/50"
              }`}
                data-testid="cazar-factura-dropzone">
                <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
                {file ? (
                  <>
                    <div className="text-sm font-semibold text-emerald-300">{file.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{(file.size / 1024).toFixed(1)} KB · Listo para procesar</div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold">Arrastra o haz click para seleccionar</div>
                    <div className="text-xs text-zinc-500 mt-1">PDF / PNG / JPG — Máx 20MB</div>
                  </>
                )}
              </div>
              <input id="cf-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden" data-testid="cazar-factura-input" />
            </label>

            <div className="flex justify-end gap-2">
              {file && <Button variant="ghost" onClick={reset} className="text-zinc-400">Quitar</Button>}
              <Button onClick={submit} disabled={!file || processing}
                data-testid="cazar-factura-submit"
                className="bg-violet-500 hover:bg-violet-400 text-zinc-950 font-semibold">
                {processing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analizando con IA…</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Analizar factura</>}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            {/* Resumen cabecera */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <FileText className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="text-sm font-semibold">Factura {result.factura.numero || "(sin número)"}</div>
                  <div className="text-xs text-zinc-500">{result.factura.fecha} · {result.factura.emisor_nombre} · NIF {result.factura.emisor_nif}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-bold text-amber-300">{(result.factura.total || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €</div>
                  <div className="text-[10px] text-zinc-500">Base {(result.factura.base_imponible || 0).toLocaleString("es-ES")} · IVA {(result.factura.iva || 0).toLocaleString("es-ES")}</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-zinc-950/40 rounded p-2"><div className="text-xl font-bold text-zinc-100">{result.resumen.lineas_factura}</div><div className="text-[10px] uppercase text-zinc-500">Líneas factura</div></div>
                <div className="bg-emerald-500/10 rounded p-2"><div className="text-xl font-bold text-emerald-300">{result.resumen.lineas_matcheadas}</div><div className="text-[10px] uppercase text-emerald-400/80">Cuadradas</div></div>
                <div className="bg-red-500/10 rounded p-2"><div className="text-xl font-bold text-red-300">{result.resumen.lineas_sin_match}</div><div className="text-[10px] uppercase text-red-400/80">Sin cuadrar</div></div>
                <div className="bg-zinc-950/40 rounded p-2"><div className="text-xl font-bold text-amber-300">{result.trabajos_encontrados}</div><div className="text-[10px] uppercase text-zinc-500">Trabajos en BD</div></div>
              </div>
            </div>

            {result.trabajos_encontrados === 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <b>Atención:</b> no se encontraron trabajos de este colaborador en el rango de la factura
                  ({result.rango_busqueda.desde} → {result.rango_busqueda.hasta}).
                  Verifica que el colaborador esté correctamente asignado a las rutas en esas fechas, o
                  comprueba si el periodo de la factura es de antes de empezar a usar la app.
                </div>
              </div>
            )}

            {/* Tabla cruce */}
            <div className="border border-zinc-800 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-950/80">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="px-2 py-2 w-8 text-center" title="Marca/desmarca para aprobar esta línea">✓</th>
                    <th className="px-3 py-2 w-1/3">Línea de factura</th>
                    <th className="px-3 py-2 text-right">Importe</th>
                    <th className="px-3 py-2 text-center">Cruce</th>
                    <th className="px-3 py-2">Trabajo emparejado</th>
                    <th className="px-3 py-2 text-right">Discrepancia</th>
                  </tr>
                </thead>
                <tbody>
                  {result.matches.map((m, i) => {
                    const lf = m.linea_factura;
                    const match = m.match;
                    const excluido = excluidos[i];
                    return (
                      <tr key={i} data-testid={`match-row-${i}`}
                        className={`border-t border-zinc-800/60 transition-all ${
                          excluido ? "opacity-40 bg-zinc-900/40" :
                          match ? "bg-emerald-500/[0.03]" : "bg-red-500/[0.04]"
                        }`}>
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox"
                            checked={!excluido}
                            onChange={() => toggleExcluir(i)}
                            data-testid={`aprobar-checkbox-${i}`}
                            className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 accent-emerald-500"
                            title={excluido ? "Marcar para aprobar" : "Desmarcar (no guardar esta línea)"}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1 text-zinc-200">
                            <Calendar className="w-3 h-3 text-zinc-500 shrink-0" />
                            <span className="text-[11px] text-zinc-400">{lf.fecha_servicio || "—"}</span>
                          </div>
                          <div className="text-xs text-zinc-300 mt-0.5">{lf.descripcion}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-zinc-100 font-mono whitespace-nowrap">
                          {(lf.importe || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
                        </td>
                        <td className="px-3 py-2 text-center">
                          {match ? (
                            <div className="inline-flex flex-col items-center">
                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                              <span className="text-[10px] text-emerald-400 mt-0.5">{match.score}/100</span>
                            </div>
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-red-400 inline" />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {match ? (
                            <div>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                <span className="text-xs font-medium text-zinc-100">{match.trabajo.espacio_nombre || "—"}</span>
                              </div>
                              <div className="text-[10px] text-zinc-500">
                                {match.trabajo.cadena_nombre} · {match.trabajo.espacio_ciudad} · {match.trabajo.fecha}
                                {match.trabajo.peticion_codigo && <span className="ml-2 text-amber-300 font-mono">[{match.trabajo.peticion_codigo}]</span>}
                              </div>
                            </div>
                          ) : (
                            <div className="text-[11px] text-red-300/80 italic">Sin cruce automático {m.top_candidatos.length > 0 && `· ${m.top_candidatos.length} candidatos descartados (score < 50)`}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right" data-testid={`discrepancia-${i}`}>
                          {match ? renderDiscrepancia(m.discrepancia) : <span className="text-[10px] text-zinc-600">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Aviso si hay sin_precio para que el usuario sepa qué hacer */}
            {(result.resumen.sin_precio_acordado || 0) > 0 && (
              <div className="bg-zinc-800/40 border border-zinc-700 rounded-lg p-3 text-xs text-zinc-300 flex gap-2"
                   data-testid="aviso-sin-precio">
                <AlertTriangle className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5" />
                <div>
                  <b>{result.resumen.sin_precio_acordado} línea(s) sin precio acordado.</b> No hay tarifa día en
                  el colaborador ni coste de montaje en la línea para comparar. Aprueba a ciegas o cancela y
                  añade el precio antes de guardar.
                </div>
              </div>
            )}

            {/* Discrepancias alto */}
            {(result.resumen.discrepancias_alto || 0) > 0 && (
              <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-xs text-red-200 flex gap-2"
                   data-testid="aviso-discrepancias">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <b>{result.resumen.discrepancias_alto} línea(s) con discrepancia GRANDE (&gt;10%)</b> respecto al
                  precio acordado. Revísalas antes de aprobar.
                </div>
              </div>
            )}

            {/* Notas */}
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Notas (opcional)</label>
              <Textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Ej.: Línea 3 confirmada por WhatsApp con Juan. Subió precio por desplazamiento extra."
                data-testid="cazar-factura-notas"
                rows={2}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs"
              />
            </div>

            {/* Footer con totales y aprobar */}
            {totalesAprobar && (
              <div className="sticky bottom-0 bg-zinc-950 -mx-6 px-6 pt-3 pb-1 border-t border-zinc-800"
                   data-testid="cazar-factura-footer">
                <div className="flex items-end justify-between gap-3">
                  <div className="text-xs text-zinc-400 space-y-0.5">
                    <div>
                      <span className="text-zinc-500">A aprobar:</span>{" "}
                      <span className="text-emerald-300 font-semibold">{totalesAprobar.nLin}</span> línea(s) ·{" "}
                      <span className="text-zinc-100 font-mono">{totalesAprobar.totFact.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €</span>
                    </div>
                    {totalesAprobar.totAcord > 0 && (
                      <div className="text-[10px]">
                        <span className="text-zinc-500">vs Acordado:</span>{" "}
                        <span className="text-zinc-300 font-mono">{totalesAprobar.totAcord.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €</span>
                        {" "}·{" "}
                        <span className={
                          Math.abs(totalesAprobar.diff) <= 1 ? "text-emerald-400" :
                          totalesAprobar.diff > 0 ? "text-red-400" : "text-amber-300"
                        }>
                          {totalesAprobar.diff > 0 ? "+" : ""}{totalesAprobar.diff.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={reset} disabled={guardando}
                      className="bg-transparent border-zinc-700 text-zinc-300">
                      Subir otra
                    </Button>
                    <Button onClick={onClose} variant="ghost" disabled={guardando} className="text-zinc-400">
                      Cancelar
                    </Button>
                    <Button onClick={aprobar} disabled={guardando || totalesAprobar.nLin === 0}
                      data-testid="cazar-factura-aprobar"
                      className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold">
                      {guardando ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Guardando…</> :
                        <><Save className="w-4 h-4 mr-1.5" /> Aprobar y guardar ({totalesAprobar.nLin})</>}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
